import React from "react"
import { Tab, TabList, TabPanel, Tabs } from "react-tabs"
import { IDps, SimulatorStatus } from "../../../../../types"
import SimulatorState from "../../../../../rooms/states/simulator-state"
import { Weather } from "../../../../../types/enum/Weather"
import GamePlayerDpsMeter from "../game/game-player-dps-meter"
import GamePlayerDpsTakenMeter from "../game/game-player-dps-taken-meter"
import GamePlayerHpsMeter from "../game/game-player-hps-meter"
import "../game/game-dps-meter.css"

export default function SimulatorBattleSummary({
  status,
  weather,
  result,
  blueDpsMeter,
  redDpsMeter
}: {
  status: SimulatorStatus
  weather: Weather
  result?: SimulatorState["result"]
  blueDpsMeter: IDps[]
  redDpsMeter: IDps[]
}) {
  const live = status === SimulatorStatus.RUNNING
  const winnerLabel = result?.winnerLabel
  const hasData = blueDpsMeter.length > 0 || redDpsMeter.length > 0

  return (
    <div className="simulator-summary my-box">
      <div className="simulator-summary-header">
        <div>
          <h2>Battle Summary</h2>
          <p className="simulator-summary-copy">
            {live
              ? "Live combat meters update as the simulation plays."
              : winnerLabel
                ? `Winner: ${winnerLabel}`
                : "Run a battle to populate the live summary meters."}
          </p>
        </div>
        <div className="simulator-summary-meta">
          <span className="simulator-summary-pill">
            {(result?.weather ?? weather).toLowerCase()}
          </span>
          <span className={`simulator-summary-pill status-${status}`}>
            {live ? "Live" : status}
          </span>
          {winnerLabel && (
            <span className="simulator-summary-pill winner">{winnerLabel}</span>
          )}
        </div>
      </div>

      <Tabs className="simulator-summary-tabs">
        <TabList>
          <Tab>Damage</Tab>
          <Tab>Tanking</Tab>
          <Tab>Heal + Shield</Tab>
        </TabList>

        <TabPanel>
          <div className="simulator-summary-grid meters">
            <SummaryTeamColumn title="Blue Team">
              {hasData ? (
                <GamePlayerDpsMeter dpsMeter={blueDpsMeter} />
              ) : (
                <p className="simulator-summary-empty">Waiting for combat data.</p>
              )}
            </SummaryTeamColumn>
            <SummaryTeamColumn title="Red Team">
              {hasData ? (
                <GamePlayerDpsMeter dpsMeter={redDpsMeter} />
              ) : (
                <p className="simulator-summary-empty">Waiting for combat data.</p>
              )}
            </SummaryTeamColumn>
          </div>
        </TabPanel>

        <TabPanel>
          <div className="simulator-summary-grid meters">
            <SummaryTeamColumn title="Blue Team">
              {hasData ? (
                <GamePlayerDpsTakenMeter dpsMeter={blueDpsMeter} />
              ) : (
                <p className="simulator-summary-empty">Waiting for combat data.</p>
              )}
            </SummaryTeamColumn>
            <SummaryTeamColumn title="Red Team">
              {hasData ? (
                <GamePlayerDpsTakenMeter dpsMeter={redDpsMeter} />
              ) : (
                <p className="simulator-summary-empty">Waiting for combat data.</p>
              )}
            </SummaryTeamColumn>
          </div>
        </TabPanel>

        <TabPanel>
          <div className="simulator-summary-grid meters">
            <SummaryTeamColumn title="Blue Team">
              {hasData ? (
                <GamePlayerHpsMeter dpsMeter={blueDpsMeter} />
              ) : (
                <p className="simulator-summary-empty">Waiting for combat data.</p>
              )}
            </SummaryTeamColumn>
            <SummaryTeamColumn title="Red Team">
              {hasData ? (
                <GamePlayerHpsMeter dpsMeter={redDpsMeter} />
              ) : (
                <p className="simulator-summary-empty">Waiting for combat data.</p>
              )}
            </SummaryTeamColumn>
          </div>
        </TabPanel>
      </Tabs>
    </div>
  )
}

function SummaryTeamColumn({
  title,
  children
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <section className="summary-column">
      <h3>{title}</h3>
      {children}
    </section>
  )
}
